"use client";

import { ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = ["Quarterly maintenance plans for proactive year-round defense", "Targeted ant and roach elimination around perimeter hot spots", "Spider web removal along eaves, porches, and entryways", "Exterior perimeter barrier protection that stops pests outside", "Eco-friendly treatment options applied with precision"];
export default function GeneralPestControl() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="pest-control" id="pest-control" className="bg-background text-foreground py-20 md:py-28 border-b border-border scroll-mt-24">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="block h-px w-8 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-primary">
                Foundational Defense
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
              General Pest Control
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6 font-sans">
              Our general pest control service forms a dependable barrier around your home’s perimeter, intercepting ants, roaches, spiders, and common insects before they cross the threshold. We combine thorough inspections with targeted exterior applications to keep insect populations under control across every season.
            </p>

            <ul className="space-y-3 mb-8">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm md:text-base text-foreground">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>)}
            </ul>

            <Button asChild className="rounded-md h-auto px-7 py-3.5 text-base font-bold font-default bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md">
              <Link to="/ContactPage">
                Get Your Free Inspection
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-border bg-card rounded-md p-8 shadow-sm">
              <div className="size-12 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-6">
                <ShieldCheck size={28} />
              </div>
              <h3 className="text-xl font-bold font-default text-foreground mb-3">
                Exterior-First Philosophy
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                By reinforcing your property&apos;s perimeter defense every three months, we intercept pests where they live and forage outdoors, minimizing the need for extensive interior treatments.
              </p>
              <div className="border-t border-border pt-4 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Targeting: Ants • Cockroaches • Spiders • Crickets
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}